// Mock data for Synentia landing page - Factory.ai style

export const services = [
  {
    id: "01",
    slug: "consulting",
    label: "Consulting",
    title: "Consulting Built for Teams That Want to Move",
    description: "We cut through the noise around AI and build a practical plan around how your business actually operates. Free audit to start — paid in-depth engagement for teams that need to go deeper.",
    link: "/consulting",
    summary: "Roadmaps, prioritization, and implementation planning",
    highlights: [
      {
        title: "Opportunity Mapping",
        detail: "Identify high-value automation opportunities across teams and workflows."
      },
      {
        title: "Execution Blueprint",
        detail: "Create a phased rollout with ownership, KPIs, and measurable business outcomes."
      },
      {
        title: "Governance Design",
        detail: "Define controls for quality, compliance, and model performance from day one."
      }
    ]
  },
  {
    id: "02",
    slug: "automation",
    label: "Automation",
    title: "Automation That Enables Your Team to Focus on What Actually Matters",
    description: "We turn manual, rules-based operations into intelligent automated systems — freeing your team from repetitive execution and giving them the bandwidth to do higher-value work.",
    link: "/automation",
    summary: "RPA, browser automation, and workflow orchestration",
    highlights: [
      {
        title: "RPA & Computer Use",
        detail: "Operate software UIs, web portals, and desktop applications automatically. We replicate human actions at machine speed — inside any system, with or without an API."
      },
      {
        title: "Browser & Web Automation",
        detail: "Extract, submit, monitor, and act across any web-based platform. Anything a human does in a browser, we automate — reliably and at scale."
      },
      {
        title: "Workflow Orchestration",
        detail: "Stitch processes across tools and teams into continuous, end-to-end pipelines. Work flows forward automatically — with controls built in where human judgment is needed."
      }
    ]
  },
  {
    id: "03",
    slug: "agent-development",
    label: "Agent Development",
    title: "Agents That Think, Act, and Deliver — Without Being Told Every Step",
    description: "Production-grade AI agents for complex, multi-step business tasks. Built to reason, use tools, process multimodal inputs, and operate reliably — from standalone agents to coordinated multi-agent systems.",
    link: "/agent-development",
    summary: "Production agents for enterprise workflows",
    highlights: [
      {
        title: "Multimodal Agents",
        detail: "Agents that process text, documents, images, and structured data together — making decisions across input types the way a skilled operator would."
      },
      {
        title: "Multi-Agent Systems",
        detail: "Specialized agents that collaborate, delegate, and hand off — building end-to-end intelligence across workflows that no single agent could handle alone."
      },
      {
        title: "Tool-Calling & Integrations",
        detail: "Agents wired directly into your systems — APIs, CRMs, ERPs, databases, and automation platforms — with the access to act, not just respond."
      }
    ]
  },
  {
    id: "04",
    slug: "voice-ai",
    label: "Voice AI",
    title: "Voice AI That Thinks Before It Speaks",
    description: "Intelligent voice agents that conduct real conversations — inbound and outbound — across any language, market, and volume. Built for accuracy, natural tone, emotional awareness, and low latency response that keeps conversations flowing.",
    link: "/voice-ai",
    summary: "Intelligent voice agents for inbound and outbound calls",
    highlights: [
      {
        title: "Thinks Before It Responds",
        detail: "Powered by reasoning-first architecture, our voice agents process context, intent, and conversation history before generating a response — not just pattern-matching to a script."
      },
      {
        title: "Emotionally Aware & Tonally Accurate",
        detail: "Detects emotional cues in the caller's voice, adapts tone in real time, and maintains natural conversational flow — so interactions feel human, not automated."
      },
      {
        title: "Sub-Second Latency. Enterprise Accuracy.",
        detail: "Responses in under a second with high transcription accuracy across accents, languages, and noisy environments — built for production volume, not demo conditions."
      }
    ]
  },
  {
    id: "05",
    slug: "product-development",
    label: "Product Development",
    title: "Zero to One — Without the Waste",
    description: "We build AI-native products from first thesis to live system — moving fast, validating early, and scaling only what works. We know where most AI products fail, and we're built to avoid it.",
    link: "/product-development",
    summary: "Build and scale AI-native product experiences",
    highlights: [
      {
        title: "Product Architecture",
        detail: "Design robust model, backend, and UX foundations for production readiness."
      },
      {
        title: "Rapid Delivery",
        detail: "Move from prototype to launch quickly with iterative releases and evaluation loops."
      },
      {
        title: "Post-Launch Optimization",
        detail: "Continuously improve accuracy, reliability, and user outcomes after deployment."
      }
    ]
  },
  {
    id: "06",
    slug: "knowledge-rag",
    label: "Knowledge & RAG",
    title: "Make Your Data the Most Powerful Asset in Your AI Stack",
    description: "Most AI systems fail not because the model is wrong — but because the data feeding it is unstructured, incomplete, or inaccessible. We build knowledge infrastructure, RAG pipelines, and fine-tuned models that give your AI systems accurate, relevant, and governed access to what your organization actually knows.",
    link: "/knowledge-rag",
    summary: "RAG pipelines, fine-tuned models, and document intelligence",
    highlights: [
      {
        title: "RAG Systems",
        detail: "Retrieval-augmented generation pipelines that surface the right information at the right moment — across documents, databases, policies, and proprietary knowledge bases — with precision and low latency."
      },
      {
        title: "Fine-Tuned Models",
        detail: "Domain-specific models trained on your data to outperform general-purpose LLMs on the tasks that matter most — classification, extraction, generation, and decision support."
      },
      {
        title: "Document Intelligence",
        detail: "Multimodal pipelines that read, extract, and act on any document format — PDFs, scanned files, images, structured and unstructured data — turning static content into actionable intelligence."
      }
    ]
  },
  {
    id: "07",
    slug: "ai-infrastructure",
    label: "AI Infrastructure",
    title: "The Backbone That Keeps Your AI Production-Ready",
    description: "Deploying AI is step one. Keeping it accurate, reliable, and improving over time is what most teams aren't equipped to do alone. We operate the AI systems we build — managing infrastructure, monitoring performance, tuning models, controlling costs, and reporting outcomes every month.",
    link: "/ai-infrastructure",
    summary: "Uptime, evals, observability, and LLMOps",
    highlights: [
      {
        title: "Uptime & Incident Response",
        detail: "24/7 monitoring, SLA-backed response, API health checks, and integration maintenance — so nothing degrades silently in production."
      },
      {
        title: "AI Evals & Observability",
        detail: "Output quality scoring, drift detection, regression testing, and monthly performance reports. Full visibility into how your AI is performing and why."
      },
      {
        title: "LLMOps & Cost Control",
        detail: "Prompt versioning, model management, token optimization, and infrastructure scaling — the operational layer that keeps AI systems efficient and audit-ready."
      }
    ]
  }
];

export const clients = [
  "OneHive Healthcare",
  "Mikaela.ai",
  "2TM",
  "Careethrive",
  "Rocket LMS",
  "NovaLedger",
  "HelioWorks"
];

export const caseStudies = [
  {
    id: 1,
    category: "Healthtech",
    isNew: true,
    client: "OneHive Healthcare",
    title: "How We Built the AI Infrastructure Powering an Entire RCM Company",
    description: "Designed and deployed the complete AI infrastructure that OneHive Healthcare runs on — prior auth automation, denial triage, EOB processing, and a full observability layer operating across the entire client portfolio.",
    link: "/case-studies/onehive-healthcare",
    image:
      "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1600&q=80"
  },
  {
    id: 2,
    category: "Healthcare",
    isNew: true,
    client: "US Oncology Practice",
    title: "Prior Auth Automation Recovering $35K+ Monthly for a 6-Physician Oncology Practice",
    description: "A prior auth automation agent and underpayment recovery system that cut manual follow-up time by 70% and recovered over $35K in missed payments within the first quarter.",
    link: "/case-studies/oncology-prior-auth",
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1600&q=80"
  },
  {
    id: 3,
    category: "Finance",
    isNew: false,
    client: "Series B Fintech",
    title: "Fraud Detection Automation That Cut Manual Review Time by 80%",
    description: "An AI fraud triage agent trained on proprietary transaction data that automatically resolves low-risk flags, routes medium-risk cases, and pre-builds analyst context packages for high-risk escalations.",
    link: "/case-studies/fintech-fraud-detection",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1600&q=80"
  },
  {
    id: 4,
    category: "Law",
    isNew: false,
    client: "Commercial Law Firm",
    title: "Contract Intelligence That Replaced 200+ Hours of Manual Clause Review Per Month",
    description: "A legal document intelligence system built on a fine-tuned model that extracts, classifies, and flags key clauses — reducing associate review time per contract from 3-4 hours to under 45 minutes.",
    link: "/case-studies/law-firm-contract-intelligence",
    image:
      "https://images.unsplash.com/photo-1565008447742-97f6f38c985c?auto=format&fit=crop&w=1600&q=80"
  }
];

export const jobOpenings = [
  {
    id: 1,
    department: "Engineering",
    title: "Senior AI Engineer",
    location: "San Francisco, CA",
    type: "Full time",
    isNew: true
  },
  {
    id: 2,
    department: "Engineering",
    title: "ML Infrastructure Engineer", 
    location: "Remote",
    type: "Full time",
    isNew: true
  },
  {
    id: 3,
    department: "Solutions",
    title: "AI Solutions Architect",
    location: "New York, NY",
    type: "Full time",
    isNew: false
  },
  {
    id: 4,
    department: "Product",
    title: "Product Manager - AI",
    location: "San Francisco, CA",
    type: "Full time",
    isNew: false
  },
  {
    id: 5,
    department: "Sales",
    title: "Enterprise Account Executive",
    location: "London, UK",
    type: "Full time",
    isNew: false
  }
];
